import React from 'react';
import CSelect, { Error } from 'src/components/form/Select';
import { ActionMeta, Props, components, OptionProps } from 'react-select';
import _ from 'lodash';
import styled, { useTheme } from 'styled-components';
import { roleOptions } from 'src/api/types';
import { Role } from 'src/graphql/_generated';
import { Tooltip } from 'src/common/Tooltip';
import { Info } from 'src/assets/dynamicSvg';

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface RoleSelectProps extends Omit<Props, 'value' | 'options'> {
  value: Role | undefined | null;
  onChange: (value: { label: string; value: Role }, meta: ActionMeta<any>) => void;
  onBlur?: () => void;
  error?: boolean;
  helperText?: string | false;
  disabled?: boolean;
  placeholder?: string;
  filteredOutRoles?: Role[];
  disabledOptions?: { value: Role; reason: string }[];
  optionTooltips?: { value: Role; reason: string }[];
}

interface OptionType {
  label: string;
  value: Role;
  isDisabled?: boolean;
  disabledReason?: string;
  tooltipReason?: string;
}

const CustomOption: React.FC<OptionProps<OptionType, false>> = (props) => {
  const { data, children } = props;
  const theme = useTheme();

  if (data.isDisabled && data.disabledReason) {
    return (
      <components.Option {...props}>
        <Tooltip
          dataId={`role-option-${data.value}`}
          dataHtml={data.disabledReason}
          place="right"
          inline
          positionStrategy="fixed"
        >
          <OptionWrapper>
            <span>{children}</span>
            <Info style={{ transform: 'scale(0.8)' }} />
          </OptionWrapper>
        </Tooltip>
      </components.Option>
    );
  }

  if (data.tooltipReason) {
    return (
      <components.Option {...props}>
        <Tooltip
          dataId={`role-option-${data.value}`}
          dataHtml={data.tooltipReason}
          place="right"
          inline
          positionStrategy="fixed"
        >
          <OptionWrapper>
            <span>{children}</span>
            <Info fill={theme.colors.prussianBlue} style={{ transform: 'scale(0.8)' }} />
          </OptionWrapper>
        </Tooltip>
      </components.Option>
    );
  }

  return <components.Option {...props} />;
};

export const RoleSelect: React.FC<RoleSelectProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
  placeholder = 'Select role',
  filteredOutRoles,
  disabledOptions,
  optionTooltips,
  ...rest
}) => {
  const baseOptions = filteredOutRoles?.length
    ? roleOptions.filter((option) => !filteredOutRoles.includes(option.value))
    : roleOptions;

  const options: OptionType[] = _.orderBy(
    baseOptions.map((option) => {
      const disabledOption = disabledOptions?.find((d) => d.value === option.value);
      const tooltipOption = optionTooltips?.find((t) => t.value === option.value);
      return {
        ...option,
        isDisabled: !!disabledOption,
        disabledReason: disabledOption?.reason,
        tooltipReason: tooltipOption?.reason,
      };
    }),
    [(option) => option.isDisabled, (option) => option.label.toLowerCase()],
    ['asc', 'asc'],
  );

  return (
    <>
      <CSelect
        isDisabled={disabled}
        value={_.find(options, (o) => o.value === value)}
        placeholder={placeholder}
        options={options}
        onChange={onChange}
        onBlur={onBlur}
        inputId="role-select"
        name="role"
        components={{ Option: CustomOption }}
        {...rest}
      />
      {error && helperText && <Error>{helperText}</Error>}
    </>
  );
};
